/**
 * Collect RUM metrics (in standalone mode) to send to [client-rum](https://github.com/optimizely/client-rum).
 *
 * [![RUM data flow](https://www.websequencediagrams.com/files/render?link=Pn6Fn4jb8JusaWAQh0xJ6kbRNsb3K1OqlQkQazvbu8PgbfoMt3XoLqSotyu3c0Tn)](https://www.websequencediagrams.com/#open=685664)
 * @module
 */
var _ = require('optimizely-client-fn');
var uuid = require('optimizely-uuid');

var AsyncRequest = require('core/utils/async_request');
var Behavior = require('core/behavior');
var DateEnv = require('core/env/date');
var DocumentEnv = require('core/env/document');
var Flux = require('core/flux');
var LocalStorage = require('core/env/local_storage').LocalStorage;
var Logger = require('core/utils/logger');
var NavigatorEnv = require('core/env/navigator');
var PromiseEnv = require('core/lib/promise').Promise;
var Visitor = require('core/visitor');
var WindowEnv = require('core/env/window');
var xhr = require('core/utils/xhr');

var actionTypes = require('core/constants/action_types');
var enums = require('core/constants/enums');

var di = require('core/di');
var AsyncRequestStore = di.get('stores/async_request');
var ClientMetadataStore = di.get('stores/client_metadata');
var GlobalStore = di.get('stores/global');
var RumStore = di.get('stores/rum');
var PerformanceStore = di.get('stores/performance');
var XDomainStore = di.get('stores/xdomain');
var ViewDataStore = di.get('stores/view_data');

var Api = require('./api');

var RUM_HOST = __RUM_HOST__;
var RUM_TIMEOUT = (__TEST__ || __EXPERIMENTAL_EDGE_SPA__ || __EXPERIMENTAL_TRACKING_ONLY__) ? 10 : __RUM_TIMEOUT__;
var RUM_VERSION = '1.0';

var sampleRate = (__RUM_SAMPLING_RATE__ >= 0) ? __RUM_SAMPLING_RATE__ : 0.003;

// Used for testing when in rum sampling
var testSampleRum = false;

/**
 * Initialize RUM data
 *
 * MUST be in the synchronous path of the client, because on async entries,
 * `getScriptSrc` returns undefined.
 */
exports.initialize = function() {
  var xid = uuid.generate().replace(/-/g, '');
  var sampleRum;
  if (__EDITOR__ || __PREVIEW__) {
    sampleRum = false;
  } else {
    sampleRum = __TEST__ ? testSampleRum : Math.random() < sampleRate;
  }

  var srcUrl = getScriptSrc();

  Flux.dispatch(actionTypes.SET_RUM_DATA, {
    id: xid,
    RumHost: RUM_HOST,
    inRumSample: sampleRum,
    src: srcUrl,
    data: {
      id: xid,
      sync: isSync(),
      timebase: DateEnv.now(),
      sampleRate: sampleRate,
      url: srcUrl,
      extras: {
        initialDOMState: DocumentEnv.getReadyState(),
      },
    }
  });
};

/**
 * Set timers to send RUM beacons
 *  - when the 'load' event fires
 *  - soon after RUM_TIMEOUT ms have elapsed
 *
 * @return {Promise} Resolves when the RUM_TIMEOUT beacon is sent.
 **/
exports.queueBeacons = function(): Promise<any> {
  if (!RumStore.getSampleRum()) {
    return PromiseEnv.resolve();
  }

  updateRenderTime();

  // If page is already loaded, send after RUM_TIMEOUT
  if (DocumentEnv.isLoaded()) {
    WindowEnv.setTimeout(sendOnLoad, RUM_TIMEOUT);
  } else {
    // Send the rest of the RUM payload on page load
    WindowEnv.addEventListener('load', sendOnLoad);
  }

  // Set up and send the RUM_TIMEOUT beacon
  return new PromiseEnv(function(resolve, reject) {
    WindowEnv.setTimeout(function() {
      prepAndSendTimeoutBeacon().then(resolve, reject);
    }, RUM_TIMEOUT);
  })
  .catch(function(err) {
    Logger.warn('RUM / Error sending data:', err);
  });
};

/**
 * @returns {Promise}
 *        The result of `send`ing the timeout beacon.
 * @private
 */
function prepAndSendTimeoutBeacon(): Promise<any> {
  updateRumData();

  var rumData = RumStore.getRumData();
  var userTimingsData = PerformanceStore.getMarks() || {};

  rumData.extras = rumData.extras || {};
  rumData.extras.beacon = {cjsTimeout: true};
  rumData.userTimings = userTimingsData;

  // Remove zero and undefined valued parameters
  rumData = _.pickBy(rumData, function(val) {
    return !_.isUndefined(val);
  });

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('RumData', rumData);
  }

  return send(rumData);
}

/**
 * Ensure beacons 2..n are not sent until the response to beacon 1
 * has been received. This allows cookie-based sticky sessions to work,
 * helping beacons to be routed to the same client-rum instance.
 *
 * @param {Function} sendFn
 *        Logic to send a beacon. This function must be suitable for giving to
 *        AsyncRequest (i.e., should manage resolving/rejecting the 'RUM_FIRST_BEACON').
 *        It will be invoked at a time when there is the strongest chance of
 *        the request being routed to the same instance.
 * @returns {Promise}
 *        Resolves with the result of sendFn.
 */
function ensureStickySessions(sendFn:Function): Promise<any> {
  var firstRequest = AsyncRequestStore.getPromise('RUM_FIRST_BEACON');

  if (firstRequest) {
    return firstRequest.then(sendFn);
  }

  return AsyncRequest.makeAsyncRequest('RUM_FIRST_BEACON', sendFn);
}

/**
 * Collect relevant RUM data and send to RUM endpoint
 * {RumData} rumData
 * @private
 * @return {Promise}
 */
function send(rumData): Promise<any> {
  if (_.isEmpty(rumData)) {
    return PromiseEnv.resolve();
  }

  return ensureStickySessions(function requestFn() {
    return xhr.request({
      url: RUM_HOST,
      method: 'POST',
      data: rumData,
      withCredentials: true,
    })
    .then(function (response) {
      // Since subsequent invocations of AsyncRequest.{resolve,reject} are no-ops,
      // every beacon can assume it's the first and resolve/reject the PromiseEnv.
      AsyncRequest.resolveRequest('RUM_FIRST_BEACON', response);
      return response;
    })['catch'](function (err) {
      Logger.error('POST to client-rum failed:', err);
      AsyncRequest.rejectRequest('RUM_FIRST_BEACON', err);
      throw err;
    });
  });
}

/**
 * @returns {string}
 *        The snippet src. If called async, returns undefined.
 *        See https://stackoverflow.com/questions/38769103/document-currentscript-is-null
 * @private
 */
function getScriptSrc(): string {
  var scr = DocumentEnv.getCurrentScript();
  if (scr) {
    return scr.src;
  }
}

/**
 *  RUM data combining base project info, resourcetiming info and render time
 * Port from classic client - https://github.com/optimizely/optimizely/blob/c9d0f24cf530427ed52d227c7f3b2b00797f3ba7/src/www/js/client/rum.js#L305
 * @private
 */
function updateRumData() {
  var rumData = {
    // Use snippetId in place of projectId if defined: ensure that we can compare "like" snippets
    'id': RumStore.getRumId(),
    'v': RUM_VERSION,
    'account': GlobalStore.getAccountId(),
    'project': GlobalStore.getSnippetId() ||
      GlobalStore.getProjectId(),
    'snippet': GlobalStore.getSnippetId(),
    'revision': GlobalStore.getRevision(),
    'clientVersion': ClientMetadataStore.getClientVersion(),
    'hasSlave': __SLAVE__,
    'wxhr': true,
    extras: {},
  };

  var previousEventCount = Visitor.getPersistedBehaviorEventCount();
  var currentEventCount = Behavior.getEventCount();
  rumData['numBehaviorEvents'] = currentEventCount;
  var diff = currentEventCount - previousEventCount;
  _.extend(rumData.extras, {
    behaviorEventCountDiff: diff,
    behaviorEventCountDecreased: diff < 0
  });

  _.assign(rumData, getResourceTimingData(), getPerformanceTimings());

  Flux.dispatch(actionTypes.SET_RUM_DATA, {
    data: rumData,
  });
}

/**
 * Get resource timing data from resource timing api
 * @return {Object|undefined} Resource timing data collected using PerformanceResourceTiming API, or undefined when API not supported
 * @private
 */
function getResourceTimingData(): any|undefined {
  var perf = WindowEnv.getGlobal('performance');
  if (!perf) {
    return;
  }
  var scriptSrc = RumStore.getScriptSrc();
  var timing;
  try {
    if (scriptSrc) {
      Logger.debug('Using derived script src: ', scriptSrc);
      var timings = perf.getEntriesByName(scriptSrc);
      if (timings.length > 0) {
        timing = timings[0];
      }
    }

    if (!timing) {
      // Fallback only works on non-custom snippets
      // This will also fail for self hosting
      var optiRegex = /\/\/[^.]+\.optimizely\.(com|test)\/(js|api\/client)\/[\d]+\.js/gi;
      Logger.debug('Scanning resource timing entries with regex');
      var resourceTimings = perf.getEntriesByType('resource');
      timing = _.find(resourceTimings, function(eachTiming) {
        return optiRegex.test(eachTiming.name);
      });
    }

    if(timing) {
      return _.mapValues(enums.ResourceTimingAttributes, function(val, key) {
        var timingVal = timing[key];
        if (typeof timingVal === 'number') {
          return Math.round((timingVal || 0) * 1e3) / 1e3;
        } else if (key === 'serverTiming') {
          return timingVal || [];
        }
      });
    }
  } catch(e) {
    return;
  }
}

/**
 * if the script is loaded synchronously
 * @return {boolean}
 * @private
 */
function isSync(): boolean {
  try {
    return !DocumentEnv.querySelector('body');
  } catch(e) {
    return null;
  }
}

/**
 * Get the time to first render after client loads and update the store
 * @private
 */
function updateRenderTime() {
  try {
    WindowEnv.getGlobal('requestAnimationFrame')(function() {
      var timebase = RumStore.getRumData().timebase;
      Flux.dispatch(actionTypes.SET_RUM_DATA, {
        data: {
          'render': DateEnv.now() - (timebase || 0),
        },
      });
    });
  } catch(e) {
    return;
  }
}

/**
 * Get performance marks
 * @return {Object.<number>|undefined} Map of measureName to duration for included measures
 * @private
 */
function getPerformanceTimings(): Record<string, number>|undefined {
  return PerformanceStore.getDurationsFor(_.values(enums.RUMPerformanceTimingAttributes));
}

/**
 * Metrics to try to answer questions like "what share of the visitor's localStorage is
 * Optimizely using? How is it distributed between categories (events, event_queue, etc.)
 * of items? Is it correlated with the number of origins?".
 *
 * @returns {PromiseEnv.<LocalStorageMetric>}
 *        Surely fulfills.
 * @private
 */
function getLocalStorageMetrics(): Promise<LocalStorageMetric> {
  var keys = LocalStorage.keys();

  var keyInfos = _.filter(_.map(keys, function (k) {
    var category = Visitor.getStorageKeyFromKey(k);
    if (!category) {
      // Consider items whose category we can't identify as items
      // that aren't owned by Optimizely. As a result, leave them out
      // of storage metrics.
      return null;
    }

    return {
      key: k,
      isForeign: Visitor.isForeignKey(k),
      category: category,
      size: k.length + LocalStorage.getItem(k).length,
    };
  }));

  // Accumulate the IDs within `keys` into 'local' and 'foreign' sets.
  // Note that an ID may be in both sets, if it is present in both local and foreign keys.
  var ids = _.reduce(keyInfos, function (acc, info) {
    var key = info.key;
    var id = Visitor.getIdFromKey(key);
    if (!id) return acc;

    var map = info.isForeign ? acc.foreign : acc.local;
    map[id] = true;

    return acc;
  }, { local: {}, foreign: {} });

  var foreignOrigins = _.chain(keyInfos)
    .filter({ isForeign: true })
    .reduce(function (acc, info) {
      var origin = info.key.split('_')[0];
      acc[origin] = true;
      return acc;
    }, {})
    .value();

  var byteTotals = { local: 0, foreign: 0 };
  var byteTotalsByCategory = { local: {}, foreign: {} };
  _.forEach(keyInfos, function (info) {
    var locality = info.isForeign ? 'foreign' : 'local';

    byteTotals[locality] += info.size;

    if (!byteTotalsByCategory[locality][info.category]) {
      byteTotalsByCategory[locality][info.category] = 0;
    }
    byteTotalsByCategory[locality][info.category] += info.size;
  });

  var synchronousMetrics = {
    // Total localStorage metrics
    numKeys: LocalStorage.allKeys().length,
    sizeKeys: LocalStorage.allKeys().toString().length,
    sizeValues: LocalStorage.allValues().toString().length,

    idCounts: {
      local: _.keys(ids.local).length,
      foreign: _.keys(ids.foreign).length,
    },
    foreignOriginCount: _.keys(foreignOrigins).length,

    byteTotals: byteTotals,
    byteTotalsByCategory: byteTotalsByCategory,
  };

  var storageEstimate = NavigatorEnv.estimateStorage();

  return storageEstimate.then(function (estimate) {
    return _.assign(synchronousMetrics, {
      storageEstimate: estimate,
    });
  });
}

/**
 * Get rum data to send on load
 * @private
 */
function sendOnLoad() {
  // Update navigation Timings Data
  var perf = WindowEnv.getGlobal('performance');
  var navtimings = perf ? perf.timing : {};

  // Update with user timings data
  var userTimingsData = PerformanceStore.getMarks() || {};
  var apiData = RumStore.getApiData();
  var DOMObservationData = RumStore.getDOMObservationData();
  // TODO(CJS-3611): get experiment IDs (at least the number) from edge tracking snippets
  var experimentIds = __EXPERIMENTAL_EDGE_SPA__ || __EXPERIMENTAL_TRACKING_ONLY__ ? null : Api.get('state').getActiveExperimentIds();
  var featuresNeeded = RumStore.getFeaturesNeededData();

  var parsedUri = DocumentEnv.parseUri(RumStore.getScriptSrc());

  var data = RumStore.getRumData() || {};
  var extras = data.extras || {};
  _.assign(extras, {
    apiCalls: apiData,
    DOMObservationData: DOMObservationData,
    paintTimings: getPaintTimings(),
    activeExperimentIds: experimentIds,
    numPages: ViewDataStore.getNumberOfPages(),
    snippet: {
      // Discard trailing ':'
      scheme: parsedUri.protocol.slice(0, -1),
      host: parsedUri.host,
      path: parsedUri.pathname,
    },
    networkInfo: getNetworkInformation(),
    experimental: GlobalStore.getExperimental(),
    featuresNeeded: featuresNeeded,
    beacon: {
      cjsOnload: true,
    },
  });

  // Misc library usage
  var prototypeJS = WindowEnv.getGlobal('Prototype');
  // 1.7.3: http://api.prototypejs.org/Prototype/Version/ (also works on 1.6.1 but undocumented)
  if (prototypeJS && !_.isUndefined(prototypeJS.Version)) {
    extras.prototypeJS = prototypeJS.Version;
  }

  var xdomainEnabled = false;
  if (__XDOMAIN__) {
    xdomainEnabled = true;
    var frames = XDomainStore.getFrames();
    if (frames.length) {
      extras.xdFramesLoaded = frames.length;
    }
  }

  var synchronousRumData = {
    id: RumStore.getRumId(),
    v: RUM_VERSION,
    project: GlobalStore.getSnippetId() ||
      GlobalStore.getProjectId(),
    navigationTimings: navtimings,
    userTimings: userTimingsData,
    xd: xdomainEnabled,
    apis: _.keys(apiData),
    extras: extras,
    sampleRate: data.sampleRate,
  };

  getLocalStorageMetrics().then(function (lsMetrics) {
    var mergedData = _.assign(synchronousRumData, {
      lsMetrics: lsMetrics,
    });

    if (__INVARIANT__) {
      require('core/utils/invariant').isType('RumDataOnLoad', mergedData);
    }

    send(mergedData);
  });
}

/**
 * Get network type and connection metrics from browser
 * https://developer.mozilla.org/en-US/docs/Web/API/Network_Information_API
 * @return {Object|undefined} Map of network information
 * @private
 */
function getNetworkInformation(): any|undefined {
  var nav = WindowEnv.getGlobal('navigator');
  if (nav && nav.connection) {
    return _.pick(nav.connection, ['downlink', 'rtt', 'effectiveType']);
  }
}

/**
 * Get paint timings, if supported. See https://developer.mozilla.org/en-US/docs/Web/API/PerformancePaintTiming
 * @return {Object.<number>|undefined} Map of timing name (e.g. "first-paint", "first-contentful-paint") to startTime
 * @private
 */
function getPaintTimings(): Record<string, number>|undefined {
  var perf = WindowEnv.getGlobal('performance');
  if (!perf) {
    return;
  }
  try {
    var paintTimings = perf.getEntriesByType('paint');
    if (_.isEmpty(paintTimings)) {
      return;
    }
    return _.reduce(paintTimings, function(res, timing) {
      res[timing.name] = Math.round(timing.startTime);
      return res;
    }, {});
  } catch(e) {
    return;
  }
}

if (__TEST__) {
  exports.RUM_HOST = RUM_HOST;
  exports.getLocalStorageMetrics = getLocalStorageMetrics;
  exports.sampleRate = sampleRate;
  exports.send = send;
  exports.inRumSample = function(sampleRum) {
    testSampleRum = sampleRum;
  };
}
